import { computed, ref } from 'vue'
import { Dialog } from 'quasar'
import { newFormData } from 'js/utils/GLOBAL'
import { perguntasService } from 'services/perguntas.service'
import {
  relacaoTipoPergunta,
  tipoEnvioEvidencia,
} from 'js/utils/constants/pergunta'
import { respostasService } from 'services/respostas.service'
import { statusEvidencia } from 'js/utils/GLOBAL'
import { storeToRefs } from 'pinia'
import { useEvidenciaStore } from 'store/evidencia.store'
import { useI18n } from 'vue-i18n'
import { usePerguntaStore } from 'store/pergunta.store'
import { useRoute } from 'vue-router'
import ModalSweetAlert from 'components/Modal/ModalSweetAlert.vue'
import useApiError from './useApiError'
import useAssessment from './useAssessment'

/**
 * @typedef {import('../types/pergunta').Itemopcaoset} Itemopcaoset
 * @typedef {import('../types/pergunta').Alternativaset} Alternativaset
 */
export default function usePerguntas() {
  const { t } = useI18n()
  const route = useRoute()

  const {
    getPergunta,
    getPlanoAcao,
    patchPerguntaEdit,
    postPlanoAcao,
    patchPlanoAcao,
    getAcompanhamento,
    postAcompanhamento,
    postAcompanhamentoFile,
  } = perguntasService()

  const { postRespostaButton, postRespostaLivre } = respostasService()
  const { modalEvidenciaCreateRef, modalEvidenciaRef } = storeToRefs(
    useEvidenciaStore()
  )

  const { assessment } = useAssessment()

  const perguntaStore = usePerguntaStore()

  const {
    pergunta,
    alternativas,
    respostas,
    model,
    loading,
    planoAcao,
    visualizacao,
    acompanhamentos,
    heightAlternativa,
    modalPlanoAcaoRef,
    modalAcompanhamentoRef,
    modalEditarCamposRef,
  } = storeToRefs(perguntaStore)

  const headerRef = ref(null)

  const onOpenModalEvidenciaCreate = () => {
    modalEvidenciaCreateRef.value?.open()
  }

  const onOpenModalPlanoAcao = () => {
    modalPlanoAcaoRef.value?.open()
  }

  const onOpenModalAcompanhamento = () => {
    modalAcompanhamentoRef.value?.open()
  }

  const onOpenModalEvidencia = () => {
    modalEvidenciaRef.value?.open()
  }

  const onOpenModalEditarCampos = () => {
    modalEditarCamposRef.value?.open()
  }

  const tipoOpcao = computed(() => {
    const tipo = pergunta.value.tipo
    return relacaoTipoPergunta(tipo)
  })

  const tipoEvidencia = computed(() => {
    const tipo = pergunta.value.tipo_envio_evidencia
    return tipoEnvioEvidencia(tipo)
  })

  const isCorporateGroup = computed(() => {
    return window.context.user.grupo_corporativo
  })

  const isTabPrevious = computed(() => {
    return route.query.tab === 'previous'
  })

  const isDisabledResposta = computed(() => {
    return isCorporateGroup.value || isTabPrevious.value
  })

  const hasEvidencia = computed(() => {
    return alternativas.value.some((alt) => alt.tem_evidencia)
  })

  async function initialRequests(idPergunta, params = {}) {
    perguntaStore.resetStore()
    loading.value.pergunta = true

    try {
      const reqPergunta = await getPergunta(idPergunta, {
        periodo: route.query.period,
        ...params,
      })

      pergunta.value = reqPergunta
      verificaRespondidas()
      handleVisualizacaoChange()
    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.pergunta = false
    }
  }

  async function initialRequestsPlanoAcao(idPergunta, idFabricante) {
    loading.value.planoAcao = true
    try {
      const req = await getPlanoAcao(
        `?pergunta=${idPergunta}&fabricante=${idFabricante}`
      )
      planoAcao.value = req.results.at()
    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.planoAcao = false
    }
  }

  async function initialRequestsAcompanhamento(idAcompanhamento) {
    loading.value.acompanhamentos = true
    try {
      const req = await getAcompanhamento(
        `?acompanhamento=${idAcompanhamento}`
      )
      acompanhamentos.value = req?.results
    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.acompanhamentos = false
    }
  }

  async function sendPerguntaEdit(payload) {
    loading.value.edit = true
    try {
      const id = payload.id
      await patchPerguntaEdit(id, payload)

      modalEditarCamposRef.value.close()
      showModalConfirm({ text: t('dataUpdatedSuccessfully') })

      await initialRequests(id)
    } catch (error) {
      console.error(error)
    } finally {
      loading.value.edit = false
    }
  }

  function verificaRespondidas() {
    pergunta.value.alternativa_set.forEach((alternativa) => {
      alternativa.itemopcao_set.forEach((itemopcao) => {
        if (itemopcao?.item_resposta?.id) {
          const respostasAnteriores =
            respostas.value?.[alternativa.id]?.respostas || []
          respostas.value[alternativa.id] = {
            respostas: [...respostasAnteriores, itemopcao],
          }
        }
      })
      return alternativa
    })
  }

  /**
   * @description desmarca outras respostas que tem a mesma opcao
   * @param {Number} opcao
   * @param {Alternativaset} alternativa  */
  async function regraParaDesmarcar(itemopcao, alternativa) {
    const _respostas = { ...respostas.value }

    // Itera sobre todas as alternativas
    for (const id in _respostas) {
      _respostas[id].respostas =
        _respostas[id].respostas?.filter(
          (r) => r.opcao !== itemopcao.opcao
        ) || [] // Remove a opcao selecionada de todas as alternativas
    }

    respostas.value = _respostas
  }

  /** @description remove ao clicar novamente no mesmo item */
  async function regraParaRemover(itemopcao, alternativa) {
    const temResposta = respostas.value[
      alternativa.id
    ]?.respostas?.find((r) => r.opcao === itemopcao.opcao)

    if (temResposta) {
      respostas.value[alternativa.id].respostas = respostas.value[
        alternativa.id
      ].respostas.filter((r) => r.opcao !== itemopcao.opcao)
      return true
    }
  }

  async function sendResposta(itemopcao) {
    loading.value.enviandoResposta = true
    try {
      await postRespostaButton({
        item_opcao: itemopcao.id,
        resposta: pergunta.value.resposta,
      })
    } catch (e) {
      useApiError(e)
    } finally {
      loading.value.enviandoResposta = false
    }
  }

  async function sendPlanoAcao(payload) {
    loading.value.enviandoPlanoAcao = true
    try {
      await postPlanoAcao(payload)
      modalPlanoAcaoRef.value.close()
      showModalConfirm({ text: t('actionPlanSuccessfully1') })
      await initialRequestsPlanoAcao(
        payload.pergunta,
        payload.fabricante
      )
    } catch (e) {
      useApiError(e)
    } finally {
      loading.value.enviandoPlanoAcao = false
    }
  }

  async function sendAcompanhamento(payload) {
    loading.value.enviandoAcompanhamento = true
    try {
      const acompanhamento = await postAcompanhamento(payload)
      const formData = newFormData({
        arquivo: payload.files,
      })
      await postAcompanhamentoFile(acompanhamento.id, formData)
      modalAcompanhamentoRef.value.close()
      showModalConfirm({
        text: t('actionSumarySeccessFull'),
      })
      await initialRequestsAcompanhamento(payload.acompanhamento)
    } catch (e) {
      useApiError(e)
    } finally {
      loading.value.enviandoAcompanhamento = false
    }
  }

  async function updatePlanoAcao(id, payload) {
    loading.value.enviandoPlanoAcao = true
    try {
      await patchPlanoAcao(id, payload)
      modalPlanoAcaoRef.value.close()
      showModalConfirm({
        text: t('actionPlanSuccessfully'),
      })
      await initialRequestsPlanoAcao(
        payload.pergunta,
        payload.fabricante
      )
    } catch (e) {
      useApiError(e)
    } finally {
      loading.value.enviandoPlanoAcao = false
    }
  }

  async function showModalConfirm(props) {
    Dialog.create({
      component: ModalSweetAlert,
      componentProps: {
        type: 'sucess',
        timeout: 2500,
        ...props,
      },
    })
  }

  /** @param {{ itemopcao:Itemopcaoset: alternativa: Alternativaset }} params  */
  async function onClickButton({ itemopcao, alternativa }) {
    const removed = await regraParaRemover(itemopcao, alternativa)
    if (removed) {
      await sendResposta(itemopcao)
      return
    }

    await regraParaDesmarcar(itemopcao)
    const respostasAnteriores =
      respostas.value?.[alternativa.id]?.respostas || []

    respostas.value[alternativa.id] = {
      respostas: [...respostasAnteriores, itemopcao],
    }
    await sendResposta(itemopcao)
  }

  async function onUpdateOpt({ itemopcao, value, livre }) {
    loading.value.enviandoResposta = true
    try {
      const res = livre
        ? {
            resposta_livre: livre.value,
            tipo_resposta_livre: livre.tipo,
          }
        : { resposta_select: value }

      await postRespostaLivre({
        item_opcao: itemopcao.id,
        resposta: pergunta.value.resposta,
        ...res,
      })
    } catch (e) {
      useApiError(e)
    } finally {
      loading.value.enviandoResposta = false
    }
  }

  function onClickEvidencia({ evidencia, pergunta, alternativa }) {
    const status = statusEvidencia(evidencia?.status)
    if (status?.aprovada || status?.pendente || status?.reprovada) {
      modalEvidenciaRef.value?.open({
        data: {
          evidencia,
          pergunta,
          alternativa,
        },
      })
    } else {
      modalEvidenciaCreateRef.value?.open({
        data: { alternativa, pergunta },
      })
    }
  }

  function onClickButtonEvidencia({ sendAll }) {
    const alternativaComEvidencia = alternativas.value.find((alt) => {
      return alt.tem_evidencia
    })
    if (alternativaComEvidencia) {
      if (sendAll) {
        modalEvidenciaRef.value?.open({
          header: {
            title: t('evidences'),
            showProgress: false,
            showEvidenciaNavigation: false,
          },
          data: {
            pergunta,
            alternativas: alternativas.value,
            evidencia: alternativaComEvidencia.evidencia,
          },
        })
      } else {
        modalEvidenciaRef.value?.open({
          data: {
            pergunta,
            alternativa: alternativaComEvidencia,
            evidencia: alternativaComEvidencia.evidencia,
          },
        })
      }
    } else {
      modalEvidenciaCreateRef.value?.open({
        data: { sendAll },
        header: { title: t('evidences'), showProgress: false },
      })
    }
  }

  function onClickPeriods(period) {
    initialRequests(pergunta.value.id, {
      request_period:
        period?.id || assessment.value?.first_previous_period,
    })
  }

  function onClickCurrent() {
    initialRequests(pergunta.value.id)
  }

  /**
   * Altera o modo de visualização.
   * Ao atribuir um novo valor a `visualizacao.value`,
   * o modo de visualização é atualizado para o valor especificado.
   * e permanece persistente com o uso do plugin de persistência do Pinia.
   * @param {'column' | 'row'} view - O modo de visualização desejado.
   */
  function onClickView(view) {
    visualizacao.value = view
  }

  /**
   * Manipula a mudança de visualização.
   * Se o tipo de opção não for tradicional,
   * a visualização é definida como 'row'.
   */
  function handleVisualizacaoChange() {
    if (!tipoOpcao.value.tradicional) {
      visualizacao.value = 'row'
    }
  }

  return {
    acompanhamentos,
    alternativas,
    initialRequests,
    initialRequestsAcompanhamento,
    initialRequestsPlanoAcao,
    sendPerguntaEdit,
    loading,
    modalAcompanhamentoRef,
    modalEditarCamposRef,
    modalEvidenciaCreateRef,
    modalEvidenciaRef,
    modalPlanoAcaoRef,
    model,
    visualizacao,
    onClickButton,
    onClickPeriods,
    onClickCurrent,
    onClickView,
    onClickEvidencia,
    onClickButtonEvidencia,
    onOpenModalAcompanhamento,
    onOpenModalEditarCampos,
    onOpenModalEvidencia,
    onOpenModalEvidenciaCreate,
    onOpenModalPlanoAcao,
    onUpdateOpt,
    pergunta,
    planoAcao,
    respostas,
    sendAcompanhamento,
    sendPlanoAcao,
    tipoOpcao,
    tipoEvidencia,
    hasEvidencia,
    isTabPrevious,
    isCorporateGroup,
    isDisabledResposta,
    updatePlanoAcao,
    headerRef,
    heightAlternativa,
  }
}
